import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch, getToken } from "@/api";
import { Button } from "@/components/ui/button";

export default function RenewalPack() {
  const { vendorKey } = useParams();
  const [pack, setPack] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ decision: "renew", justification: "", agreed: "yes" });
  const [status, setStatus] = useState(null);
  const [canWrite, setCanWrite] = useState(false);

  const load = () =>
    apiFetch(`/vendors/${vendorKey}/renewal-pack`).then(async (r) => {
      if (!r.ok) { setError("This renewal pack is not available to you."); return; }
      setPack(await r.json());
    });

  useEffect(() => {
    if (!getToken()) { setError("Sign in to view this renewal pack."); return; }
    apiFetch("/auth/my-permissions").then((r) => (r.ok ? r.json() : { permissions: [] }))
      .then((d) => setCanWrite(d.permissions.includes("contract.write")));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorKey]);

  const signOff = async () => {
    setStatus("Recording sign-off…");
    const r = await apiFetch(`/vendors/${vendorKey}/renewal-signoff`, {
      method: "POST",
      body: JSON.stringify({ decision: form.decision, justification: form.justification,
                              agreed_with_suggestion: form.agreed === "yes" }),
    });
    const body = await r.json();
    setStatus(r.ok ? "Sign-off recorded and applied to this vendor's contracts"
                   : `Sign-off failed — ${JSON.stringify(body.detail)}`);
    if (r.ok) load();
  };

  if (error) return <p className="p-8 text-sm" data-testid="pack-error">{error}</p>;
  if (!pack) return <p className="p-8 text-sm" data-testid="pack-loading">Preparing…</p>;

  const card = pack.scorecard;
  const rec = pack.recommendation;

  return (
    <main className="min-h-screen bg-white text-black">
      <style>{`@media print { .no-print { display: none } main { padding: 0 } }`}</style>
      <div className="max-w-3xl mx-auto p-8 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold" data-testid="pack-title">{pack.name}</h1>
            <p className="text-sm text-neutral-600" data-testid="pack-as-of">
              Prepared {new Date(pack.as_of).toLocaleString()} for {pack.prepared_for} ·
              synthetic non-production data
            </p>
          </div>
          <div className="flex gap-2 no-print">
            <Button size="sm" variant="outline" data-testid="pack-print"
                    onClick={() => window.print()}>Print</Button>
            <Button asChild size="sm" variant="outline" data-testid="pack-back">
              <Link to="/contracts">Contracts</Link>
            </Button>
          </div>
        </div>

        <section className="border rounded-md p-4 space-y-2" data-testid="pack-recommendation">
          <h2 className="text-lg font-medium">
            Suggestion: <span data-testid="pack-suggestion">{rec.suggestion}</span>
          </h2>
          <p className="text-sm font-medium text-amber-800" data-testid="pack-suggestion-label">
            {rec.label}
          </p>
          <ul className="text-sm list-disc pl-5" data-testid="pack-evidence">
            {rec.evidence.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
          <p className="text-xs text-neutral-600">Method: {rec.method}</p>
        </section>

        <section className="border rounded-md p-4 space-y-2" data-testid="pack-signoff">
          <h2 className="text-lg font-medium">Owner sign-off</h2>
          {pack.sign_off ? (
            <div className="text-sm space-y-1" data-testid="pack-signoff-record">
              <p>
                <span className="font-medium">
                  {pack.sign_off.decision.replace(/_/g, " ")}
                </span>{" "}
                — {pack.sign_off.justification}
              </p>
              <p className="text-neutral-700">
                Signed by {pack.sign_off.signed_by_email} on{" "}
                {new Date(pack.sign_off.signed_at).toLocaleString()} · platform suggested{" "}
                {pack.sign_off.platform_suggestion} ·{" "}
                <span data-testid="pack-signoff-agreement">
                  {pack.sign_off.override ? "owner overrode the suggestion"
                    : "owner agreed with the suggestion"}
                </span>
              </p>
              <p className="text-neutral-700">
                Applied to {pack.sign_off.contracts.join(", ")}
              </p>
            </div>
          ) : canWrite ? (
            <div className="space-y-2 no-print" data-testid="pack-signoff-form">
              <p className="text-sm text-neutral-700">
                Recording a decision here applies it to every active contract for this vendor.
              </p>
              <select className="border rounded-md px-2 py-2 text-sm" aria-label="Decision"
                      data-testid="signoff-decision" value={form.decision}
                      onChange={(e) => setForm({ ...form, decision: e.target.value })}>
                {["renew", "renegotiate", "terminate", "let_lapse"].map((d) => (
                  <option key={d} value={d}>{d.replace(/_/g, " ")}</option>
                ))}
              </select>
              <select className="border rounded-md px-2 py-2 text-sm ml-2"
                      aria-label="Do you agree with the platform's suggestion?"
                      data-testid="signoff-agreement" value={form.agreed}
                      onChange={(e) => setForm({ ...form, agreed: e.target.value })}>
                <option value="yes">I agree with the suggestion ({rec.suggestion})</option>
                <option value="no">I am overriding the suggestion</option>
              </select>
              <input className="block w-full border rounded-md px-3 py-2 text-sm"
                     placeholder="Justification (required)" aria-label="Justification"
                     data-testid="signoff-justification" value={form.justification}
                     onChange={(e) => setForm({ ...form, justification: e.target.value })} />
              <Button size="sm" variant="outline" data-testid="signoff-save" onClick={signOff}>
                Record sign-off
              </Button>
              {status && <p className="text-sm text-amber-800" data-testid="signoff-status">{status}</p>}
            </div>
          ) : (
            <p className="text-sm text-neutral-700" data-testid="pack-signoff-readonly">
              No sign-off on file. Recording one needs contract write access.
            </p>
          )}
        </section>

        <section className="space-y-2" data-testid="pack-performance">
          <h2 className="text-lg font-medium">Performance</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            {[["SLA attainment", card.sla.attainment_pct === null
              ? "—" : `${card.sla.attainment_pct}%`, "sla"],
              ["Measured requests", card.sla.sample_size, "sample"],
              ["Requests total", card.demand.requests_total, "demand"],
              ["Outages", card.reliability.outages, "outages"],
              ["Major incidents", card.reliability.major_incidents, "major"],
              ["Survey responses", card.satisfaction.responses, "surveys"],
              ["CIs", card.estate.cis, "cis"],
              ["Assets", card.estate.assets, "assets"]].map(([label, value, id]) => (
              <div key={id} className="border rounded px-3 py-2" data-testid={`pack-${id}`}>
                <p className="text-xs text-neutral-600">{label}</p>
                <p className="text-lg font-semibold">{value}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-neutral-600" data-testid="pack-method">{card.method}</p>
        </section>

        <section className="space-y-2" data-testid="pack-credits">
          <h2 className="text-lg font-medium">Service credits</h2>
          <p className="text-sm">
            Owed and unclaimed: {pack.credits.owed_unclaimed} · recovered:{" "}
            {pack.credits.recovered}
          </p>
          <ul className="text-sm" data-testid="pack-credit-rows">
            {pack.credits.periods.slice(0, 12).map((p) => (
              <li key={`${p.number}-${p.period}`}>
                {p.number} · {p.period} · {p.amount ?? p.credit_due} ·{" "}
                {p.claim ? p.claim.status : "owed"} · attainment {p.attainment_pct}%
              </li>
            ))}
            {pack.credits.periods.length === 0 && (
              <li className="text-neutral-600">No credits have been triggered.</li>
            )}
          </ul>
        </section>

        <section className="space-y-2" data-testid="pack-obligations">
          <h2 className="text-lg font-medium">Obligations</h2>
          <p className="text-sm" data-testid="pack-obligation-summary">
            {pack.obligation_summary.total} recorded ·{" "}
            {pack.obligation_summary.missed} missed · {pack.obligation_summary.overdue} overdue ·
            met rate{" "}
            {pack.obligation_summary.met_rate_pct === null
              ? "not yet assessable" : `${pack.obligation_summary.met_rate_pct}%`}
          </p>
          <ul className="text-sm" data-testid="pack-obligation-rows">
            {pack.obligations.slice(0, 15).map((o) => (
              <li key={o.id}>
                {o.kind}: {o.title} · due {o.due_date} · {o.state} · {o.owner_email}
              </li>
            ))}
            {pack.obligations.length === 0 && (
              <li className="text-neutral-600">No obligations recorded on this vendor yet.</li>
            )}
          </ul>
        </section>

        <section className="space-y-2" data-testid="pack-contracts">
          <h2 className="text-lg font-medium">Contracts and risk</h2>
          <ul className="text-sm">
            {card.contracts.map((c) => (
              <li key={c.number} data-testid={`pack-contract-${c.number}`}>
                {c.number} · {c.title} · ends {c.term_end} ·{" "}
                {c.renewal.state.replace(/_/g, " ")} · Legal ref {c.source_reference}
              </li>
            ))}
          </ul>
          <p className="text-sm" data-testid="pack-risk">
            BAA: {card.risk.baa_status} · security review age{" "}
            {card.risk.security_review_age_days ?? "not recorded"} days · exit plan{" "}
            {card.risk.exit_plan_on_file ? "on file" : "missing"}
          </p>
          {card.commitment ? (
            <p className="text-sm" data-testid="pack-commitment">
              Annual commitment {card.commitment.currency}{" "}
              {card.commitment.annual_value_total.toLocaleString()} — {card.commitment.note}
            </p>
          ) : (
            <p className="text-sm" data-testid="pack-commitment-withheld">
              {card.commitment_withheld}
            </p>
          )}
          <p className="text-xs text-neutral-600" data-testid="pack-authority">
            {pack.authority_note}
          </p>
        </section>
      </div>
    </main>
  );
}
